import { useCallback, useEffect, useMemo, useState } from 'react';

const EMPTY_ASSIGNED_IDS = new Set<number>();

interface UseAssignedFieldsDraftArgs {
  open: boolean;
  initialAssignedOrder: readonly number[];
  onSaveFields?: (assignedIds: ReadonlySet<number>) => void;
}

export function useAssignedFieldsDraft({
  open,
  initialAssignedOrder,
  onSaveFields
}: UseAssignedFieldsDraftArgs) {
  const [draftAssignedIds, setDraftAssignedIds] =
    useState<ReadonlySet<number>>(EMPTY_ASSIGNED_IDS);

  const initialAssignedIds = useMemo(
    () => new Set(initialAssignedOrder),
    [initialAssignedOrder]
  );

  useEffect(() => {
    if (open) {
      setDraftAssignedIds(initialAssignedIds);
    }
  }, [open, initialAssignedIds]);

  const hasAssignmentChanges =
    draftAssignedIds.size !== initialAssignedIds.size ||
    [...draftAssignedIds].some(id => !initialAssignedIds.has(id));

  const isSaveAssignmentsDisabled = !onSaveFields || !hasAssignmentChanges;

  const handleSaveAssignments = useCallback(() => {
    if (!onSaveFields) return;
    onSaveFields(draftAssignedIds);
  }, [draftAssignedIds, onSaveFields]);

  return {
    draftAssignedIds,
    setDraftAssignedIds,
    isSaveAssignmentsDisabled,
    handleSaveAssignments
  };
}
